// src/context/ShopContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot, runTransaction, collection, serverTimestamp, setDoc, addDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { getPaths } from '../firebase.paths';
import { globalAppId, globalFirebaseConfig, SHOP_ITEMS, SHOP_ITEM_IMAGE_FILENAMES } from '../utils/constants';
import { getCachedSync } from '../services/imageService';
import { isUserOnVacation } from '../utils/userUtils';

const ShopContext = createContext();

export const useShopData = () => useContext(ShopContext);

// ─── Constrói a URL de uma imagem do Storage ─────────────────────────────────
// Tenta o cache do ImageService primeiro (populado pelo pré-carregamento do
// ImageContext assim que o app inicia). Só constrói a URL direta se não houver
// cache — sem nenhum round-trip extra de rede.
const bucket = globalFirebaseConfig?.storageBucket || 'ranking-rp-metodos-4aad2.firebasestorage.app';

const resolveUrl = (filename) => {
    if (!filename) return null;
    if (filename.startsWith('http')) return filename;
    return getCachedSync(filename) ||
        `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(filename)}?alt=media`;
};

// ─── Resolve todas as URLs de imagem da loja de forma síncrona ───────────────
// Usada como lazy initializer no useState — rodada uma única vez na montagem,
// antes da primeira renderização, sem esperar nenhum useEffect.
const buildShopImageUrls = () => {
    const itemUrls = {};
    const categoryUrls = {};

    SHOP_ITEMS.flatMap(c => c.items).forEach(item => {
        const fname = item.imageFilename || SHOP_ITEM_IMAGE_FILENAMES[item.name];
        if (fname) itemUrls[item.name] = resolveUrl(fname);
    });

    SHOP_ITEMS.forEach(cat => {
        if (cat.image) categoryUrls[cat.category] = resolveUrl(cat.image);
    });

    return { itemUrls, categoryUrls };
};

export const ShopProvider = ({ children }) => {
    const { db, userProfile, auth } = useAuth();
    const paths = useMemo(() => getPaths(globalAppId), []);

    const [shopItemsStock, setShopItemsStock] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // URLs resolvidas de forma síncrona no primeiro render, usando cache quando disponível
    const [shopItemImageUrls] = useState(() => buildShopImageUrls().itemUrls);
    const [shopCategoryImageUrls] = useState(() => buildShopImageUrls().categoryUrls);
    const [shopCategoryMarcaUrl] = useState(() => resolveUrl('RPImagens/RP_Logo_NovoCompacto.jpeg'));

    // Monitorar Estoque
    useEffect(() => {
        if (!db) return;
        const unsub = onSnapshot(doc(db, paths.config, 'shopSettings'), (snap) => {
            if (snap.exists()) setShopItemsStock(snap.data());
            setIsLoading(false);
        });
        return () => unsub();
    }, [db, paths]);

    // Resgate de item
    const handleRedeemCredits = async (cost, itemName) => {
        const userId = auth.currentUser?.uid;
        if (!userId) throw new Error("Usuário não logado.");
        if (isUserOnVacation(userProfile)) throw new Error("Ação bloqueada: Você está em período de férias.");
        if (userProfile?.shopAccessUntil && new Date() < new Date(userProfile.shopAccessUntil.seconds * 1000)) {
            throw new Error("Acesso à loja bloqueado temporariamente.");
        }
        if ((userProfile?.credits || 0) < cost) throw new Error("Saldo insuficiente.");

        const staticItem = SHOP_ITEMS.flatMap(cat => cat.items).find(i => i.name === itemName);
        const currentConfig = shopItemsStock[itemName] || {};
        const itemRealCost = Number(currentConfig.price !== undefined ? currentConfig.price : (staticItem?.realCost || 0));

        await runTransaction(db, async (transaction) => {
            const userRef = doc(db, paths.users, userId);
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) throw new Error("Perfil não encontrado.");

            // Valida créditos dentro da transação para garantir atomicidade
            const currentCredits = userSnap.data().credits || 0;
            if (currentCredits < cost) throw new Error("Saldo insuficiente.");

            const configRef = doc(db, paths.config, 'shopSettings');
            const configSnap = await transaction.get(configRef);
            const stockData = configSnap.exists() ? configSnap.data() : {};

            const currentStock = stockData[itemName]?.stock;
            const itemStock = currentStock !== undefined ? Number(currentStock) : (staticItem?.initialStock || 100);

            if (itemStock <= 0) throw new Error("Item esgotado.");

            transaction.update(userRef, { credits: currentCredits - cost });
            transaction.set(configRef, { [itemName]: { ...stockData[itemName], stock: itemStock - 1 } }, { merge: true });

            const transRef = doc(collection(db, paths.transactions));
            transaction.set(transRef, {
                targetUserId: userId,
                targetUserName: userProfile.name,
                pointsChange: 0,
                creditsChange: -cost,
                valueBRL: -Math.abs(itemRealCost),
                reason: `Resgate: ${itemName}`,
                type: 'redemption',
                itemRefunded: itemName,
                status: 'approved',
                deliveryStatus: 'pending',
                createdAt: serverTimestamp()
            });
        });
    };

    const handleUpdateItemConfig = async (itemName, newData, adminName) => {
        if (!db) throw new Error("Banco de dados não inicializado");

        const configRef = doc(db, paths.config, 'shopSettings');
        const sanitizedData = {
            stock: Math.max(0, Number(newData.stock) || 0),
            price: Math.max(0, Number(newData.price) || 0),
            points: Math.max(0, Number(newData.points) || 0)
        };

        await setDoc(configRef, { [itemName]: sanitizedData }, { merge: true });

        await addDoc(collection(db, paths.transactions), {
            type: 'manual_stock_adjustment',
            itemName,
            finalStock: sanitizedData.stock,
            finalPrice: sanitizedData.price,
            adminName: adminName || 'Admin',
            createdAt: serverTimestamp(),
            status: 'approved'
        });
    };

    const value = {
        shopItemsStock,
        shopItemImageUrls,
        shopCategoryImageUrls,
        shopCategoryMarcaUrl,
        handleRedeemCredits,
        handleUpdateItemConfig,
        isLoading,
        SHOP_ITEMS
    };

    return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};
